import { Check, PencilSimpleLine, X } from "@phosphor-icons/react";
import { useState } from "react";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import { CountSpinner } from "common/components/ui/CountSpinner";
import Progress from "common/components/ui/Progress";
import {
  Table,
  TableCell,
  TableHead,
  TableRow,
} from "common/components/ui/TableV2";
import { Tag } from "common/components/ui/Tag";
import WorkspaceImage from "common/components/WorkspaceImage";
import useLicenseMutations from "common/datahooks/useLicenseMutations";
import usePromotions from "common/datahooks/usePromotions";
import useLicensedAccounts from "common/hooks/useLicensedAccounts";
import type { Workspace } from "common/types";

interface WorkspacesTableRowProps {
  workspace: Workspace;
  isEditing: boolean;
  toggleIsEditing: () => void;
}

function WorkspacesTableRow({
  workspace,
  isEditing,
  toggleIsEditing,
}: WorkspacesTableRowProps) {
  const { ltdPromotion, appSumoPromotion } = usePromotions();
  const promotion = ltdPromotion ?? appSumoPromotion;

  const {
    id,
    lifetime_license_count,
    accounts_count,
    rental_account_count,
    name,
    icon_url,
  } = workspace;
  const [seatCount, setSeatCount] = useState(lifetime_license_count);

  const { setLifetimeLicenseCount, isSettingLifetimeLicenseCount } =
    useLicenseMutations();

  const { outreachLicensedAccounts } = useLicensedAccounts(id);

  async function onSubmit() {
    if (seatCount !== lifetime_license_count && promotion) {
      await setLifetimeLicenseCount({
        workspaceId: id,
        count: seatCount,
        source: promotion.type,
        accountsToCancel: [],
      });
    }
    toggleIsEditing();
  }

  const progressValue =
    lifetime_license_count === 0
      ? 0
      : (outreachLicensedAccounts.length / lifetime_license_count) * 100;

  let actionComponent = (
    <Button
      variant="ghost-black"
      size="sm"
      intent="iconOnly"
      onClick={toggleIsEditing}
    >
      <PencilSimpleLine />
    </Button>
  );

  if (isEditing) {
    actionComponent = (
      <div className="flex gap-x-2 justify-end">
        <Button
          variant="secondary-black"
          size="sm"
          intent="iconOnly"
          disabled={isSettingLifetimeLicenseCount}
          onClick={() => {
            toggleIsEditing();
            setSeatCount(lifetime_license_count);
          }}
        >
          <X />
        </Button>
        <Button
          variant="secondary-purple"
          size="sm"
          intent="iconOnly"
          isLoading={isSettingLifetimeLicenseCount}
          onClick={onSubmit}
        >
          <Check />
        </Button>
      </div>
    );
  }

  return (
    <TableRow key={id}>
      <TableCell>
        <div className="flex gap-x-2.5 items-center">
          <WorkspaceImage id={id} imageSrc={icon_url} />
          <div className="flex flex-col items-start gap-y-0.5">
            <span className="text-md-bold">{name}</span>
            <span className="text-xs-medium text-black-500">
              {accounts_count
                ? `${accounts_count} account${accounts_count > 1 ? "s" : ""}`
                : "No accounts"}
              <RenderIf condition={!!rental_account_count}>
                {" "}
                • {rental_account_count} avatar
                {rental_account_count > 1 ? "s" : ""}
              </RenderIf>
            </span>
          </div>
        </div>
      </TableCell>

      <TableCell className="text-xs-bold">
        {outreachLicensedAccounts.length}
      </TableCell>

      <TableCell>
        <Progress value={progressValue} className="w-16" />
      </TableCell>

      <TableCell className="max-w-32">
        {isEditing && promotion ? (
          <CountSpinner
            variant="sm"
            value={seatCount}
            setValue={setSeatCount}
            maxValue={
              lifetime_license_count + promotion.unassigned_license_count
            }
            maxValueTooltipText="No more seats available"
          />
        ) : (
          <span className="text-button-xs text-black-500">
            {lifetime_license_count}
          </span>
        )}
      </TableCell>

      <TableCell>{actionComponent}</TableCell>
    </TableRow>
  );
}

interface DesktopTableProps {
  workspaces: Workspace[];
  editingWorkspaceId: string;
  setEditingWorkspaceId: (id: string) => void;
}

export default function DesktopTable({
  workspaces,
  editingWorkspaceId,
  setEditingWorkspaceId,
}: DesktopTableProps) {
  return (
    <Table>
      <thead>
        <tr>
          <TableHead>
            <div className="flex items-center gap-2">
              Workspaces
              <Tag variant="secondary" size="sm">
                {workspaces.length}
              </Tag>
            </div>
          </TableHead>
          <TableHead>Accounts</TableHead>
          <TableHead />
          <TableHead>Seats</TableHead>
          <TableHead className="w-28" />
        </tr>
      </thead>

      <tbody>
        {workspaces.map((workspace) => {
          const { id } = workspace;
          const isEditing = editingWorkspaceId === id;

          return (
            <WorkspacesTableRow
              key={id}
              workspace={workspace}
              isEditing={isEditing}
              toggleIsEditing={() => {
                setEditingWorkspaceId(isEditing ? null : id);
              }}
            />
          );
        })}
      </tbody>
    </Table>
  );
}
